import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, withHandlers, withState, withProps } from 'recompose';
import { omit } from 'lodash';

import { withClickOutside } from 'decorators';
import { GoogleMap } from 'components/core/GoogleMap';
import { SearchMarker, TogglerSearchOnMove } from 'components/search/SearchMap';

import './SearchMap.scss';

export const enhance = compose(
	withState('searchOnMove', 'setSearchOnMove', true),
	withState('searchFromMove', 'setSearchFromMove', false),
	withHandlers({
		handleMapMove:
			({ handleSearch, searchParams, searchOnMove, setSearchFromMove }) =>
			(location) => {
				if (location && searchOnMove) {
					setSearchFromMove(true);
					let locationParams = {
						...searchParams,
						location: `${location.latitude}, ${location.longitude}`,
					};
					locationParams = omit(locationParams, ['page', 'pageType']);
					handleSearch({ locationParams });
				}
			},
		// this is the handler for the checkbox that toggles the search on move
		onCheckboxChange:
			({ setSearchOnMove, searchOnMove }) =>
			() =>
				setSearchOnMove(!searchOnMove),
	}),
	// setting the props needed for the withClickOutside decorator
	withProps(
		({ handleFacilityDeselected, handlePinDeselected, pinSelected }) => ({
			outsideClickHandler: () => {
				handleFacilityDeselected();
				handlePinDeselected();
			},
			outsideParent: '.new-facility-card',
			outsideCondition: pinSelected,
		}),
	),
	withClickOutside,
);

export function SearchMap({
	className,
	facilities,
	baseUrl,
	facilitySelected,
	handleFacilityDeselected,
	handleFacilitySelected,
	handlePinSelected,
	handleMapMove,
	location,
	searchOnMove,
	searchType,
	onCheckboxChange,
	pinSelected,
	handlePinDeselected,
	searchFromMove,
	setSearchFromMove,
	searchParams,
}) {
	return (
		<div className={classnames('search-map', className)}>
			<GoogleMap
				center={location}
				locationBounds={facilities.map((facility) => facility.location)}
				onMove={handleMapMove}
				radius={5}
				searchFromMove={searchFromMove}
				setSearchFromMove={setSearchFromMove}
			>
				<TogglerSearchOnMove
					checked={searchOnMove}
					handleChange={onCheckboxChange}
				/>
				{facilities.map((facility) => (
					<SearchMarker
						key={`search-marker-${facility.id}`}
						facility={facility}
						baseUrl={baseUrl}
						handleFacilitySelected={handleFacilitySelected}
						handleFacilityDeselected={handleFacilityDeselected}
						handlePinSelected={handlePinSelected}
						handlePinDeselected={handlePinDeselected}
						location={location}
						pinSelected={pinSelected}
						selected={facilitySelected === facility.id}
						// the mini facility card should only be shown if the pin is selected
						// but not if the facility card in the search results is the one selected
						showMiniCard={
							facilitySelected === facility.id && pinSelected
						}
						searchType={searchType}
						searchParams={searchParams}
					/>
				))}
			</GoogleMap>
		</div>
	);
}

SearchMap.propTypes = {
	className: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object).isRequired,
	baseUrl: PropTypes.string,
	facilitySelected: PropTypes.number,
	handleFacilityDeselected: PropTypes.func,
	handleFacilitySelected: PropTypes.func,
	handlePinSelected: PropTypes.func,
	handleMapMove: PropTypes.func,
	location: PropTypes.object.isRequired,
	searchFromMove: PropTypes.bool,
	setSearchFromMove: PropTypes.func,
	searchOnMove: PropTypes.bool,
	searchType: PropTypes.string,
	onCheckboxChange: PropTypes.func,
	pinSelected: PropTypes.bool,
	handlePinDeselected: PropTypes.func,
	searchParams: PropTypes.object,
};

export default enhance(SearchMap);
